import axios from 'axios';
import { IST } from '../lib';
import { scopeUserFor } from '../scopeStore';
import type { WhatsAppButton, CarouselCard } from '../types';
import {
  SENDER_TYPE,
  type WhatsAppMessage,
  type InteractiveButton,
  type MessageReaction,
} from '../types/message';

// Re-export types for backward compatibility
export { SENDER_TYPE };
export type { WhatsAppMessage, InteractiveButton, MessageReaction };
export type ConversationMessage = WhatsAppMessage;

// Conversations API (Chatbot Backend)
const CONVERSATIONS_API_URL = 'https://chat-service.bhuvanafortunegreens.com';
const INTERNAL_API_KEY = 'a8f3j9k2m5n7p1q4r6s8t0u2v5w7x9y1';

const conversationsClient = axios.create({
  baseURL: CONVERSATIONS_API_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': INTERNAL_API_KEY
  }
});

conversationsClient.interceptors.request.use((config) => {
  const stored = localStorage.getItem('touchpoint_portal_user');
  if (stored) {
    try {
      const user = JSON.parse(stored);
      if (user?.id) {
        config.headers = config.headers || {};
        (config.headers as any)['X-User-Id'] = user.id;
        // The scope pick rides to the chatbot, which forwards it to core's
        // chat-visibility read — Messages follows the same book as every page.
        const picked = scopeUserFor(user.id);
        if (picked) (config.headers as any)['X-Scope-User'] = picked;
      }
    } catch {
      // malformed user — ignore, request goes without header (backend will then not filter)
    }
  }
  return config;
});

export interface SendMessageResponse {
  text: string;
  answer: string;
  in_live_agent_mode: boolean;
  sender_type: number;
  llm_log_path?: string;
}

export interface TemplateMessagePayload {
  text: string;
  header_type?: string;
  header_text?: string;
  header_media_url?: string;
  footer?: string;
  buttons?: WhatsAppButton[];
  interactive_buttons?: { id: string; title: string }[];
  carousel_cards?: CarouselCard[];
  template_id?: string;
  template_name?: string;
  flow_id?: string;
  flow_cta?: string;
}

export interface Conversation {
  id: string;
  recipient_id?: string;
  recipient_name?: string;
  recipient_avatar?: string;
  last_message?: string;
  last_message_time?: string;
  unread_count: number;
  status: string;
  title?: string;
  created_time?: string;
  lead_id?: string;
  in_live_agent_mode?: boolean;
}

const istStampFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});
const istStamp = (d: Date): string => {
  const p = Object.fromEntries(istStampFmt.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}${p.month}${p.day}${p.hour}${p.minute}${p.second}`;
};

export const conversationsApi = {
  getMessages: async (
    conversationId: string,
    opts?: { limit?: number; before?: string }
  ): Promise<{ messages: ConversationMessage[]; has_more: boolean }> => {
    const params: Record<string, string | number> = {};
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.before) params.before = opts.before;
    const response = await conversationsClient.get(`/messages/${conversationId}`, { params });
    return response.data;
  },

  sendMessage: async (conversationId: string, text: string): Promise<SendMessageResponse> => {
    const response = await conversationsClient.post('/send-message', {
      conversation_id: conversationId,
      text,
      sender_type: SENDER_TYPE.AGENT
    });
    return response.data;
  },

  sendTemplateMessage: async (conversationId: string, template: TemplateMessagePayload): Promise<SendMessageResponse> => {
    const response = await conversationsClient.post('/send-message', {
      conversation_id: conversationId,
      text: template.text,
      sender_type: SENDER_TYPE.AGENT,
      header_type: template.header_type,
      header_text: template.header_text,
      header_media_url: template.header_media_url,
      footer: template.footer,
      buttons: template.buttons,
      interactive_buttons: template.interactive_buttons,
      carousel_cards: template.carousel_cards,
      template_id: template.template_id,
      template_name: template.template_name,
      flow_id: template.flow_id,
      flow_cta: template.flow_cta,
    });
    return response.data;
  },

  sendMediaMessage: async (
    conversationId: string,
    mediaType: 'image' | 'video' | 'document',
    mediaUrl: string,
    caption?: string
  ): Promise<SendMessageResponse> => {
    const response = await conversationsClient.post('/send-message', {
      conversation_id: conversationId,
      text: caption || '',
      sender_type: SENDER_TYPE.AGENT,
      header_type: mediaType.toUpperCase(),
      header_media_url: mediaUrl,
    });
    return response.data;
  },

  sendInteractiveMessage: async (
    conversationId: string,
    bodyText: string,
    buttons: InteractiveButton[],
    footerText?: string
  ): Promise<SendMessageResponse> => {
    const response = await conversationsClient.post('/send-message', {
      conversation_id: conversationId,
      text: bodyText,
      sender_type: SENDER_TYPE.AGENT,
      interactive_type: 'button',
      interactive_buttons: buttons,
      footer_text: footerText,
    });
    return response.data;
  },

  markConversationRead: async (conversationId: string) => {
    await conversationsClient.post(`/conversation/${conversationId}/mark-read`);
  },

  getConversation: async (conversationId: string): Promise<{ in_live_agent_mode: boolean }> => {
    const response = await conversationsClient.get(`/conversation/${conversationId}`);
    return { in_live_agent_mode: response.data?.in_live_agent_mode || false };
  },

  // The chatbot DB is IST-naive (§3.23), so the window is IST wall-clock
  // `yyyyMMddHHmmss`. The server scopes the list to the caller (X-User-Id).
  getConversations: async (
    window: { from: Date; to: Date },
    limit = 20,
    offset = 0,
  ): Promise<{ conversations: Conversation[], has_more: boolean }> => {
    const response = await conversationsClient.post('/conversations/time-range', {
      start_time: istStamp(window.from),
      end_time: istStamp(window.to),
      limit,
      offset,
      sort_order: 'desc'
    });

    const rawData = response.data?.data || response.data?.conversations || (Array.isArray(response.data) ? response.data : []);
    const has_more = response.data?.has_more ?? false;

    const conversations = rawData.map((item: any) => ({
      id: item.conversation_id || item.id,
      recipient_id: item.recipient_id,
      recipient_name: item.recipient_name || item.context?.profile_name,
      recipient_avatar: item.recipient_avatar,
      last_message: item.last_message,
      last_message_time: item.updated_time || item.last_message_time,
      unread_count: item.unread_count || 0,
      status: item.is_deleted ? 'deleted' : (item.status || 'active'),
      title: item.title,
      created_time: item.created_time,
      lead_id: item.lead_id || item.context?.lead_id,
      in_live_agent_mode: item.in_live_agent_mode || false,
    }));

    return { conversations, has_more };
  },

  updateConversation: async (conversationId: string, updates: { lead_id?: string; title?: string; context?: Record<string, unknown>; in_live_agent_mode?: boolean }) => {
    await conversationsClient.put(`/conversation/${conversationId}`, updates);
  }
};

export default conversationsApi;
